import React, { useCallback, useEffect, useState } from "react";
import { api, fmtDate, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState, StatusBadge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, Paperclip } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Attachments } from "@/components/Attachments";

function Field({ label, children }) {
  return <div><Label className="label-caps mb-2 block">{label}</Label>{children}</div>;
}

const REPORT_STATUS_LABEL = { draft: "Draft", signed_off: "Signed Off", rejected: "Rejected" };

export default function CommissioningReports({ projectId, embedded = false }) {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const empty = {
    project_id: projectId || "", equipment_id: "",
    report_date: new Date().toISOString().slice(0, 10),
    engineer: "", system: "", status: "draft",
    checklist: [{ item: "", passed: true, remarks: "" }],
    readings: [{ parameter: "", value: "", unit: "", target: "", ok: true }],
    notes: "",
  };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([
      api.get("/commissioning-reports", { params: projectId ? { project_id: projectId } : {} }),
      api.get("/projects"),
      api.get("/equipment"),
    ]);
    setRows(a.data); setProjects(b.data); setEquipment(c.data);
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const projMap = Object.fromEntries(projects.map((p) => [p.id, p.name]));
  const eqMap = Object.fromEntries(equipment.map((e) => [e.id, `${e.tag_no || ""} ${e.name}`.trim()]));

  const submit = async () => {
    try {
      await api.post("/commissioning-reports", {
        ...form,
        project_id: form.project_id || projectId || projects[0]?.id,
        equipment_id: form.equipment_id || null,
        checklist: form.checklist.filter((c) => c.item),
        readings: form.readings.filter((r) => r.parameter),
      });
      toast.success("Report created");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this report?")) return;
    try { await api.delete(`/commissioning-reports/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const setCheck = (idx, patch) => setForm((f) => ({ ...f, checklist: f.checklist.map((c, i) => (i === idx ? { ...c, ...patch } : c)) }));
  const setReading = (idx, patch) => setForm((f) => ({ ...f, readings: f.readings.map((r, i) => (i === idx ? { ...r, ...patch } : r)) }));

  const formSheet = (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button data-testid="create-commissioning-button" size={embedded ? "sm" : "default"} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white">
          <Plus size={14} className="mr-1" /> New Report
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[620px] sm:max-w-2xl overflow-y-auto">
        <SheetHeader><SheetTitle className="font-display">Commissioning Report</SheetTitle></SheetHeader>
        <div className="space-y-3 mt-6">
          <div className="grid grid-cols-2 gap-3">
            <Field label="System"><Input data-testid="cr-system-input" value={form.system} onChange={(e) => setForm({ ...form, system: e.target.value })} placeholder="AHU-01 Cooling Circuit" /></Field>
            <Field label="Report date"><Input type="date" value={form.report_date} onChange={(e) => setForm({ ...form, report_date: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Engineer"><Input value={form.engineer} onChange={(e) => setForm({ ...form, engineer: e.target.value })} placeholder="Ravi Kumar" /></Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="signed_off">Signed Off</SelectItem><SelectItem value="rejected">Rejected</SelectItem></SelectContent>
              </Select>
            </Field>
          </div>
          {!projectId && (
            <Field label="Project">
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Equipment (optional)">
            <Select value={form.equipment_id || "__none__"} onValueChange={(v) => setForm({ ...form, equipment_id: v === "__none__" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {equipment.map((e) => <SelectItem key={e.id} value={e.id}>{e.tag_no} {e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          <div>
            <div className="label-caps mb-2">Checklist</div>
            <div className="space-y-2">
              {form.checklist.map((c, i) => (
                <div key={`cl-${i}`} className="grid grid-cols-[1fr_100px_1fr_24px] gap-2 items-center">
                  <Input placeholder="Check item" value={c.item} onChange={(e) => setCheck(i, { item: e.target.value })} />
                  <Select value={c.passed ? "yes" : "no"} onValueChange={(v) => setCheck(i, { passed: v === "yes" })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="yes">Pass</SelectItem><SelectItem value="no">Fail</SelectItem></SelectContent>
                  </Select>
                  <Input placeholder="Remarks" value={c.remarks} onChange={(e) => setCheck(i, { remarks: e.target.value })} />
                  <button onClick={() => setForm({ ...form, checklist: form.checklist.filter((_, x) => x !== i) })} className="text-slate-400 hover:text-red-600"><Trash size={14} /></button>
                </div>
              ))}
              <button onClick={() => setForm({ ...form, checklist: [...form.checklist, { item: "", passed: true, remarks: "" }] })} className="text-xs text-[#EA580C] font-semibold">+ ADD CHECK</button>
            </div>
          </div>

          <div>
            <div className="label-caps mb-2">Readings</div>
            <div className="space-y-2">
              {form.readings.map((r, i) => (
                <div key={`rd-${i}`} className="grid grid-cols-[1.2fr_80px_60px_80px_60px_24px] gap-2 items-center">
                  <Input placeholder="Parameter (e.g. Supply Air Temp)" value={r.parameter} onChange={(e) => setReading(i, { parameter: e.target.value })} />
                  <Input placeholder="Value" value={r.value} onChange={(e) => setReading(i, { value: e.target.value })} />
                  <Input placeholder="Unit" value={r.unit} onChange={(e) => setReading(i, { unit: e.target.value })} />
                  <Input placeholder="Target" value={r.target} onChange={(e) => setReading(i, { target: e.target.value })} />
                  <Select value={r.ok ? "ok" : "nok"} onValueChange={(v) => setReading(i, { ok: v === "ok" })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="ok">OK</SelectItem><SelectItem value="nok">Not OK</SelectItem></SelectContent>
                  </Select>
                  <button onClick={() => setForm({ ...form, readings: form.readings.filter((_, x) => x !== i) })} className="text-slate-400 hover:text-red-600"><Trash size={14} /></button>
                </div>
              ))}
              <button onClick={() => setForm({ ...form, readings: [...form.readings, { parameter: "", value: "", unit: "", target: "", ok: true }] })} className="text-xs text-[#EA580C] font-semibold">+ ADD READING</button>
            </div>
          </div>

          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <Button onClick={submit} data-testid="save-commissioning-button" className="w-full rounded-sm bg-[#0F172A] hover:bg-slate-800">Save Report</Button>
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <div data-testid="commissioning-page">
      {!embedded && (
        <PageHeader
          title="Commissioning Reports"
          subtitle="Handover documentation with checklists, readings, and PDF attachments"
          module="Service · SV"
          actions={formSheet}
        />
      )}
      {embedded && (
        <div className="flex items-center justify-between mb-4">
          <div className="label-caps">Commissioning reports ({rows.length})</div>
          {formSheet}
        </div>
      )}

      <div className={embedded ? "" : "p-8"}>
        {rows.length === 0 ? (
          <EmptyState title="No commissioning reports" description="Log system-wise commissioning readings, pass/fail checklists, and upload signed PDFs for client handover." />
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm" data-testid="commissioning-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="label-caps">Report</TableHead>
                  <TableHead className="label-caps">System</TableHead>
                  <TableHead className="label-caps">Equipment</TableHead>
                  <TableHead className="label-caps">Project</TableHead>
                  <TableHead className="label-caps">Engineer</TableHead>
                  <TableHead className="label-caps">Date</TableHead>
                  <TableHead className="label-caps">Status</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setViewing(r)} data-testid={`cr-row-${r.id}`}>
                    <TableCell className="font-mono-num font-semibold text-[#EA580C]">{r.report_no}</TableCell>
                    <TableCell className="font-medium">{r.system || "—"}</TableCell>
                    <TableCell className="text-[11px] text-slate-600">{eqMap[r.equipment_id] || "—"}</TableCell>
                    <TableCell className="text-slate-600 text-xs">{projMap[r.project_id] || "—"}</TableCell>
                    <TableCell className="text-slate-600 text-xs">{r.engineer || "—"}</TableCell>
                    <TableCell className="text-slate-600 font-mono-num text-xs">{fmtDate(r.report_date)}</TableCell>
                    <TableCell><StatusBadge status={r.status === "signed_off" ? "completed" : r.status === "rejected" ? "overdue" : "draft"} /></TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => del(r.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash size={13} /></button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">
                  <span className="font-mono text-[#EA580C]">{viewing.report_no}</span>
                  {viewing.system}
                </DialogTitle>
                <div className="text-xs text-slate-500">{fmtDate(viewing.report_date)} · {viewing.engineer || "—"} · {REPORT_STATUS_LABEL[viewing.status]}</div>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                {(viewing.checklist || []).length > 0 && (
                  <div>
                    <div className="label-caps mb-2">Checklist</div>
                    <table className="w-full text-sm border border-slate-200">
                      <thead className="bg-slate-50"><tr><th className="p-2 text-left label-caps">Item</th><th className="p-2 label-caps w-20">Result</th><th className="p-2 text-left label-caps">Remarks</th></tr></thead>
                      <tbody>{viewing.checklist.map((c, i) => (
                        <tr key={`chk-${i}-${c.item || ""}`} className="border-t border-slate-100"><td className="p-2">{c.item}</td><td className={`p-2 text-center text-[10px] font-semibold ${c.passed ? "text-emerald-600" : "text-red-600"}`}>{c.passed ? "PASS" : "FAIL"}</td><td className="p-2 text-slate-600">{c.remarks || "—"}</td></tr>
                      ))}</tbody>
                    </table>
                  </div>
                )}
                {(viewing.readings || []).length > 0 && (
                  <div>
                    <div className="label-caps mb-2">Readings</div>
                    <table className="w-full text-sm border border-slate-200">
                      <thead className="bg-slate-50"><tr><th className="p-2 text-left label-caps">Parameter</th><th className="p-2 text-right label-caps">Value</th><th className="p-2 label-caps">Unit</th><th className="p-2 text-right label-caps">Target</th><th className="p-2 label-caps w-16">OK</th></tr></thead>
                      <tbody>{viewing.readings.map((r, i) => (
                        <tr key={`rd-${i}-${r.parameter || ""}`} className="border-t border-slate-100"><td className="p-2">{r.parameter}</td><td className="p-2 text-right font-mono-num">{r.value}</td><td className="p-2">{r.unit}</td><td className="p-2 text-right font-mono-num text-slate-500">{r.target}</td><td className={`p-2 text-center text-[10px] font-semibold ${r.ok ? "text-emerald-600" : "text-red-600"}`}>{r.ok ? "OK" : "!"}</td></tr>
                      ))}</tbody>
                    </table>
                  </div>
                )}
                {viewing.notes && <div><div className="label-caps mb-1">Notes</div><div className="text-slate-700 whitespace-pre-wrap">{viewing.notes}</div></div>}
                <div className="pt-3 border-t border-slate-200">
                  <div className="label-caps mb-2 flex items-center gap-1"><Paperclip size={12} /> Signed PDF / attachments</div>
                  <Attachments parentType="commissioning_report" parentId={viewing.id} category="commissioning" />
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
